import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Loader2, Sparkles, Camera, Home, Rocket, Share2, X, ArrowRight, ArrowLeft, AlertTriangle,
  Brain, Building2, Upload, Trash2, ChevronUp, ChevronDown, Star, PartyPopper, Coins, Eye, RefreshCw,
} from "lucide-react";
import { authApi, uploadMemberFile } from "./authApi";
import { OneClickGenerate } from "./OneClickGenerate";
import { BuyCreditsModal } from "./Credits";

const STEPS = [
  { key: "imported", label: "Contenu importé", Ic: Eye },
  { key: "texts", label: "Contenus IA", Ic: Brain },
  { key: "photos", label: "Modifier & photos", Ic: Camera },
  { key: "staging", label: "Home Staging", Ic: Home },
  { key: "review", label: "Vérification", Ic: Check },
  { key: "publish", label: "Publication", Ic: Rocket },
];

// Analyse l'annonce et renvoie l'état de complétude de chaque info clé (ok=complet, sinon à corriger).
function getChecklist(l) {
  if (!l) return [];
  const imgs = (l.images || []).length;
  const desc = String(l.description || l.summary || "").trim();
  const price = Number(l.price) || 0;
  return [
    { key: "title", label: "Titre", ok: !!(l.title && String(l.title).trim()), hint: "Titre manquant" },
    { key: "price", label: price > 0 ? `${price.toLocaleString("fr-FR")} €` : "Prix", ok: price > 0, hint: "Prix à 0 ou manquant" },
    { key: "city", label: l.city || l.department || "Ville", ok: !!(l.city || l.department), hint: "Localisation manquante" },
    { key: "surface", label: Number(l.surface) > 0 ? `${l.surface} m²` : "Surface", ok: Number(l.surface) > 0, hint: "Surface manquante" },
    { key: "photos", label: `${imgs} photo${imgs > 1 ? "s" : ""}`, ok: imgs >= 3, hint: imgs === 0 ? "Aucune photo" : "Moins de 3 photos" },
    { key: "desc", label: "Description", ok: desc.length >= 120, hint: desc.length === 0 ? "Description absente" : "Description trop courte" },
  ];
}

// Bandeau récap : rappelle en un coup d'œil les infos déjà remplies (doré si à corriger).
function WizardRecap({ listing }) {
  if (!listing) return null;
  const items = getChecklist(listing);
  const { score } = getScore(listing);
  return (
    <div className="mb-4 flex items-center gap-3" data-testid="wizard-recap">
      <ScoreRing score={score} size={52} />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Qualité de l'annonce</p>
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => (
            <span key={it.key} data-testid={`recap-${it.key}`} title={it.ok ? "" : it.hint}
              className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border ${it.ok ? "text-zinc-300 border-white/15 bg-white/5" : "text-[#C19A6B] border-[#C19A6B]/50 bg-[#C19A6B]/10"}`}>
              {it.ok ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {it.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Score de complétude sur 100 (pondéré) pour motiver l'agent à tout remplir.
function getScore(l) {
  if (!l) return { score: 0, items: [] };
  const imgs = (l.images || []).length;
  const desc = String(l.description || l.summary || "").trim();
  const price = Number(l.price) || 0;
  const staged = (l.staging || []).length;
  const items = [
    { key: "title", label: "Titre", w: 10, e: l.title && String(l.title).trim() ? 10 : 0 },
    { key: "price", label: "Prix", w: 15, e: price > 0 ? 15 : 0 },
    { key: "city", label: "Localisation", w: 10, e: l.city || l.department ? 10 : 0 },
    { key: "surface", label: "Surface", w: 10, e: Number(l.surface) > 0 ? 10 : 0 },
    { key: "photos", label: "Photos", w: 25, e: imgs >= 5 ? 25 : imgs >= 3 ? 18 : imgs >= 1 ? 10 : 0 },
    { key: "desc", label: "Description", w: 20, e: desc.length >= 300 ? 20 : desc.length >= 120 ? 14 : desc.length > 0 ? 7 : 0 },
    { key: "staging", label: "Home staging", w: 10, e: staged >= 1 ? 10 : 0 },
  ];
  return { score: items.reduce((a, b) => a + b.e, 0), items };
}

const scoreColor = (s) => (s >= 80 ? "#4a9d6e" : s >= 50 ? "#C19A6B" : "#d16a4a");
const scoreLabel = (s) => (s >= 80 ? "Excellente annonce" : s >= 50 ? "Bonne base, à compléter" : "À compléter avant publication");

// Anneau de score circulaire (SVG).
function ScoreRing({ score = 0, size = 56 }) {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  const col = scoreColor(score);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="wizard-score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.5s ease, stroke 0.3s" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-bold" style={{ color: col, fontSize: size * 0.3 }} data-testid="wizard-score-value">{score}</span>
    </div>
  );
}

// Assistant de publication guidé : enchaîne création -> contenus IA -> photos ->
// home staging -> vérification -> publication -> partage réseaux, avec pop-ups.
export function PublishWizard({ slug, onClose, onEdit, onGoSocial }) {
  const [step, setStep] = useState(0);
  const [listing, setListing] = useState(null);
  const [firstListing, setFirstListing] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  // 1re annonce du membre : on masque les incitations "crédits insuffisants / passer à Premium"
  // pour une génération 100 % fluide (crédit "Annonce test" offert à l'inscription).
  useEffect(() => {
    authApi.wallet().then((w) => { if (mountedRef.current) setFirstListing((w?.listings_count ?? 99) <= 1); }).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    try { const l = await authApi.getListing(slug); if (mountedRef.current) setListing(l); return l; } catch (_) { return null; }
  }, [slug]);
  useEffect(() => { reload(); }, [reload]);

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const goPublished = () => setStep(5);

  return (
    <div className="fixed inset-0 z-[75] bg-black/75 backdrop-blur-sm overflow-y-auto py-8 px-4" data-testid="publish-wizard">
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-w-2xl mx-auto bg-[#0d0d10] border border-[#C19A6B]/25 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header + progression */}
        <div className="relative px-6 pt-5 pb-4 border-b border-white/10">
          <button onClick={onClose} data-testid="wizard-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          <p className="calli text-2xl text-[#C19A6B] leading-none">Assistant de publication</p>
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-1.5 flex-1 last:flex-none">
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${i < step ? "bg-[#C19A6B] text-white" : i === step ? "bg-[#C19A6B] text-white ring-2 ring-[#C19A6B]/40" : "bg-white/10 text-zinc-500"}`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                {i < STEPS.length - 1 && <span className={`h-0.5 flex-1 rounded ${i < step ? "bg-[#C19A6B]" : "bg-white/10"}`} />}
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-400 mt-2 uppercase tracking-wider">Étape {step + 1} / {STEPS.length} — {STEPS[step].label}</p>
        </div>

        <div className="p-6">
          {step >= 1 && step <= 4 && <WizardRecap listing={listing} />}
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              {step === 0 && <StepImported slug={slug} listing={listing} reload={reload} onNext={next} />}
              {step === 1 && <StepTexts slug={slug} onNext={next} onBack={prev} />}
              {step === 2 && <StepPhotos slug={slug} listing={listing} reload={reload} onNext={next} onBack={prev} />}
              {step === 3 && <StepStaging slug={slug} listing={listing} reload={reload} firstListing={firstListing} onPublished={goPublished} onNext={() => { reload(); next(); }} onBack={prev} />}
              {step === 4 && <StepReview slug={slug} listing={listing} firstListing={firstListing} onEdit={() => onEdit && onEdit(listing)} reload={reload} onNext={next} onBack={prev} />}
              {step === 5 && <StepPublished slug={slug} onShare={() => onGoSocial && onGoSocial(slug)} onClose={onClose} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

const Btn = ({ children, ...p }) => (
  <button {...p} className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#A88358] disabled:opacity-50 text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors">{children}</button>
);

const BackBtn = ({ onClick, ...p }) => (
  <button type="button" onClick={onClick} data-testid="wizard-back-btn" {...p} className="inline-flex items-center justify-center gap-2 border border-white/20 text-zinc-300 hover:border-[#C19A6B] hover:text-[#C19A6B] font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors">
    <ArrowLeft className="w-4 h-4" /> Précédent
  </button>
);

function StepImported({ slug, listing, reload, onNext }) {
  const [cleanAll, setCleanAll] = useState(null);
  const doCleanAll = async () => {
    const list = [...((listing && listing.images) || [])];
    if (!list.length) return;
    setCleanAll({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i++) {
      try { await authApi.cleanPhoto(slug, list[i]); } catch (_) {}
      setCleanAll({ done: i + 1, total: list.length });
    }
    setCleanAll(null); reload && reload();
  };
  if (!listing) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-[#C19A6B] animate-spin mx-auto" /></div>;
  const imgs = listing.images || [];
  const facts = [
    listing.price ? `${Number(listing.price).toLocaleString("fr-FR")} €` : null,
    listing.city || listing.department || null,
    listing.surface ? `${listing.surface} m²` : null,
    listing.rooms ? `${listing.rooms} pièces` : null,
    listing.bedrooms ? `${listing.bedrooms} ch.` : null,
  ].filter(Boolean);
  const desc = listing.description || listing.summary || "";
  return (
    <div className="space-y-4" data-testid="wizard-step-imported">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-3"><Eye className="w-7 h-7 text-[#C19A6B]" /></div>
        <h3 className="text-xl font-light text-white">Vérifiez le contenu récupéré</h3>
        <p className="text-sm text-zinc-400 mt-1.5">Voici ce que nous avons importé depuis le lien. Vous pourrez tout modifier et compléter à l'étape suivante.</p>
      </div>

      <div className="border border-white/10 rounded-xl p-4 space-y-3" data-testid="imported-content">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Titre</p>
          <p className="text-white font-medium" data-testid="imported-title">{listing.title || <span className="text-amber-400">Aucun titre récupéré</span>}</p>
        </div>
        {facts.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="imported-facts">
            {facts.map((f, i) => <span key={i} className="text-xs text-[#C19A6B] bg-[#C19A6B]/10 border border-[#C19A6B]/25 rounded-full px-2.5 py-1">{f}</span>)}
          </div>
        )}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Description</p>
          {desc ? <p className="text-sm text-zinc-300 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-line" data-testid="imported-desc">{desc}</p>
            : <p className="text-sm text-amber-400">Aucune description récupérée — l'IA la générera à l'étape « Contenus IA ».</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Photos récupérées {imgs.length > 0 ? `(${imgs.length})` : ""}</p>
          {imgs.length > 0 && (
            <button type="button" onClick={doCleanAll} disabled={!!cleanAll} data-testid="imported-clean-all"
              className="inline-flex items-center gap-1.5 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-60 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded transition-colors">
              {cleanAll ? <><Loader2 className="w-3 h-3 animate-spin" /> {cleanAll.done}/{cleanAll.total}…</> : <><Sparkles className="w-3 h-3" /> Supprimer tous les filigranes</>}
            </button>
          )}
        </div>
        {imgs.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="imported-photos">
            {imgs.slice(0, 12).map((im, i) => <img key={i} src={im} alt="" className="w-full h-20 object-cover rounded-lg border border-white/10" />)}
          </div>
        ) : (
          <p className="text-sm text-amber-400 bg-amber-400/10 border border-amber-400/25 rounded-lg px-3 py-2" data-testid="imported-nophoto">Aucune photo trouvée sur ce lien — vous pourrez en ajouter à l'étape suivante.</p>
        )}
      </div>

      <div className="flex items-center justify-center pt-1">
        <Btn onClick={onNext} data-testid="wizard-imported-next">Modifier & compléter <ArrowRight className="w-4 h-4" /></Btn>
      </div>
    </div>
  );
}

function StepTexts({ slug, onNext, onBack }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [prog, setProg] = useState(null);
  const [sim, setSim] = useState(0);
  const [err, setErr] = useState("");
  const pollRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; if (pollRef.current) clearTimeout(pollRef.current); }; }, []);
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => setSim((p) => (p >= 95 ? 95 : p + 2)), 550);
    return () => clearInterval(id);
  }, [busy]);

  const run = async () => {
    if (busy || done) return;
    setSim(4); setErr(""); setProg({ total: 2, done: 0, label: "Initialisation…" }); setBusy(true);
    const finish = () => { authApi.generateListingContent(slug).then((d) => authApi.updateListing(slug, { social: d.social || {} })).catch(() => {}); setBusy(false); setDone(true); };
    const poll = async () => {
      if (!mounted.current) return;
      try {
        const s = await authApi.generationStatus(slug);
        if (!mounted.current) return;
        if (s.progress && s.progress.total) setProg(s.progress);
        if (s.status === "done") { finish(); return; }
        if (s.status === "error") { setBusy(false); setErr(s.error || "Erreur pendant la génération"); return; }
      } catch (_) {}
      if (mounted.current) pollRef.current = setTimeout(poll, 2000);
    };
    // Déclenche la génération sans bloquer, poll immédiat à la résolution, et poll périodique en parallèle.
    authApi.generateAll(slug, { include_text: true, images: [], styles: [] })
      .then(() => { if (mounted.current) { clearTimeout(pollRef.current); poll(); } })
      .catch(() => {});
    pollRef.current = setTimeout(poll, 2000);
  };
  const realPct = prog && prog.total ? Math.round((prog.done / prog.total) * 100) : 0;
  const pct = done ? 100 : Math.min(99, Math.max(sim, realPct));
  const lbl = (prog?.label || "").toLowerCase();
  const stepInfo = /commune|ville|quartier|local|secteur|activ|école|ecole|commerce|transport/.test(lbl)
    ? { Ic: Building2, t: "Analyse de la commune", e: "Écoles, commerces, transports et activités à proximité…" }
    : /réseau|reseau|social|partage/.test(lbl) ? { Ic: Share2, t: "Publications réseaux", e: "Rédaction des textes de partage…" }
    : { Ic: Brain, t: "Génération des textes", e: "Titre, description, résumé, points forts et SEO…" };
  const Ic = stepInfo.Ic;

  return (
    <div className="space-y-4" data-testid="wizard-step-texts">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-3"><Sparkles className="w-7 h-7 text-[#C19A6B]" /></div>
        <h3 className="text-xl font-light text-white">Générons les contenus de votre annonce</h3>
        <p className="text-sm text-zinc-400 mt-1.5">En un clic : présentation, résumé, points forts, commune, écoles, commerces, transports, activités, SEO et textes réseaux.</p>
      </div>

      {busy && (
        <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/[0.06] rounded-xl p-5 text-center" data-testid="wizard-texts-progress">
          <div className="flex justify-center mb-3">
            <span className="relative w-20 h-20 rounded-full bg-[#C19A6B]/15 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full border-2 border-[#C19A6B]/40 border-t-[#C19A6B] animate-spin" />
              <span className="text-lg font-bold text-[#C19A6B]" data-testid="wizard-texts-pct">{pct}%</span>
            </span>
          </div>
          <p className="text-white font-semibold text-sm flex items-center justify-center gap-2"><Ic className="w-4 h-4 text-[#C19A6B]" /> {stepInfo.t}</p>
          <p className="text-xs text-zinc-400 mt-0.5 mb-3">{stepInfo.e}</p>
          <div className="h-2.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#d1af84] to-[#C19A6B] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="flex items-center justify-between text-[11px] text-zinc-400 mt-1.5"><span className="truncate">{prog?.label || "Création de la fiche…"}</span><span className="font-bold text-[#C19A6B]">{pct}% terminé</span></div>
        </div>
      )}
      {done && <p className="text-sm text-[#C19A6B] flex items-center gap-2 justify-center" data-testid="wizard-texts-done"><Check className="w-4 h-4" /> Vos contenus ont été générés avec succès.</p>}
      {err && <p className="text-sm text-red-400 text-center" data-testid="wizard-texts-error">{err}</p>}

      <div className="flex items-center justify-center gap-3 pt-1">
        {onBack && <BackBtn onClick={onBack} />}
        {!done ? (
          <Btn onClick={run} disabled={busy} data-testid="wizard-texts-generate">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4" /> Générer les contenus</>}</Btn>
        ) : (
          <Btn onClick={onNext} data-testid="wizard-texts-next">Continuer <ArrowRight className="w-4 h-4" /></Btn>
        )}
      </div>
    </div>
  );
}

function StepPhotos({ slug, listing, reload, onNext, onBack }) {
  const [imgs, setImgs] = useState([]);
  const [cover, setCover] = useState("");
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [city, setCity] = useState("");
  const [surface, setSurface] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState("");
  const clean = async (im) => {
    setCleaning(im);
    try { await authApi.cleanPhoto(slug, im); await reload(); } catch (_) {}
    setCleaning("");
  };
  useEffect(() => {
    if (listing) {
      setImgs(listing.images || []);
      setCover(listing.cover || (listing.images || [])[0] || "");
      setTitle(listing.title || "");
      setPrice(listing.price != null ? String(listing.price) : "");
      setCity(listing.city || "");
      setSurface(listing.surface != null ? String(listing.surface) : "");
      setDescription(listing.description || listing.summary || "");
    }
  }, [listing]);

  const add = async (file) => {
    if (!file) return; setUploading(true);
    try { const d = await uploadMemberFile(file); setImgs((p) => [...p, d.url]); } catch (_) {}
    setUploading(false);
  };
  const del = (u) => setImgs((p) => p.filter((x) => x !== u));
  const [cleanAll, setCleanAll] = useState(null);
  const doCleanAll = async () => {
    const list = [...imgs];
    if (!list.length) return;
    setCleanAll({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i++) {
      try { await authApi.cleanPhoto(slug, list[i]); } catch (_) {}
      setCleanAll({ done: i + 1, total: list.length });
    }
    setCleanAll(null); await reload();
  };
  const move = (i, dir) => setImgs((p) => { const a = [...p]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const cont = async () => {
    setSaving(true);
    try {
      const patch = { images: imgs, cover: cover || imgs[0] || "", title: title.trim(), city: city.trim(), description };
      const p = parseInt(String(price).replace(/[^\d]/g, ""), 10); if (!isNaN(p)) patch.price = p;
      const s = parseInt(String(surface).replace(/[^\d]/g, ""), 10); if (!isNaN(s)) patch.surface = s;
      await authApi.updateListing(slug, patch); await reload();
    } catch (_) {}
    setSaving(false); onNext();
  };

  const fld = "w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] placeholder:text-zinc-500";
  return (
    <div className="space-y-4" data-testid="wizard-step-photos">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-3"><Camera className="w-7 h-7 text-[#C19A6B]" /></div>
        <h3 className="text-xl font-light text-white">Modifiez le contenu & les photos</h3>
        <p className="text-sm text-zinc-400 mt-1.5">Corrigez les informations et gérez vos photos (ajout, suppression, ordre, couverture).</p>
      </div>

      {/* Édition directe des champs */}
      <div className="border border-white/10 rounded-xl p-4 space-y-3" data-testid="wizard-edit-fields">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Titre</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de l'annonce" className={fld} data-testid="wizard-field-title" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Prix (€)</label><input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="numeric" placeholder="189000" className={fld} data-testid="wizard-field-price" /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Ville</label><input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Chantilly" className={fld} data-testid="wizard-field-city" /></div>
          <div><label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Surface (m²)</label><input value={surface} onChange={(e) => setSurface(e.target.value)} inputMode="numeric" placeholder="85" className={fld} data-testid="wizard-field-surface" /></div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Description du bien (l'IA pourra l'enrichir à l'étape suivante)" className={`${fld} resize-y`} data-testid="wizard-field-description" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">Photos ({imgs.length})</p>
        {imgs.length > 0 && (
          <button type="button" onClick={doCleanAll} disabled={!!cleanAll} data-testid="wizard-clean-all"
            className="inline-flex items-center gap-1.5 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-60 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded transition-colors">
            {cleanAll ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Nettoyage {cleanAll.done}/{cleanAll.total}…</> : <><Sparkles className="w-3.5 h-3.5" /> Supprimer tous les filigranes</>}
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="wizard-photos-grid">
        {imgs.map((im, i) => (
          <div key={im + i} className={`relative group border-2 rounded-lg overflow-hidden ${cover === im ? "border-[#C19A6B]" : "border-transparent"}`}>
            <img src={im} alt="" className="w-full h-20 object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} className="bg-white/20 hover:bg-white/40 rounded p-1"><ChevronUp className="w-3 h-3 text-white" /></button>
                <button onClick={() => move(i, 1)} className="bg-white/20 hover:bg-white/40 rounded p-1"><ChevronDown className="w-3 h-3 text-white" /></button>
                <button onClick={() => del(im)} data-testid={`wizard-photo-del-${i}`} className="bg-red-500/70 hover:bg-red-500 rounded p-1"><Trash2 className="w-3 h-3 text-white" /></button>
                <button onClick={() => clean(im)} disabled={cleaning === im} data-testid={`wizard-photo-clean-${i}`} title="Nettoyer le filigrane (IA)" className="bg-white/20 hover:bg-[#C19A6B] rounded p-1">{cleaning === im ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <Sparkles className="w-3 h-3 text-white" />}</button>
              </div>
              <button onClick={() => setCover(im)} className="text-[9px] uppercase tracking-wider text-white bg-[#C19A6B]/80 hover:bg-[#C19A6B] rounded px-2 py-0.5 flex items-center gap-1"><Star className="w-2.5 h-2.5" /> Couverture</button>
            </div>
            {cover === im && <span className="absolute top-1 left-1 bg-[#C19A6B] text-white text-[8px] uppercase px-1.5 py-0.5 rounded">Couv.</span>}
          </div>
        ))}
        <label className="border-2 border-dashed border-white/20 rounded-lg h-20 flex flex-col items-center justify-center cursor-pointer hover:border-[#C19A6B]/50 transition-colors" data-testid="wizard-photo-add">
          {uploading ? <Loader2 className="w-5 h-5 text-[#C19A6B] animate-spin" /> : <Upload className="w-5 h-5 text-[#C19A6B]" />}
          <span className="text-[10px] text-zinc-400 mt-1">Ajouter</span>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && add(e.target.files[0])} />
        </label>
      </div>

      <div className="flex items-center justify-center gap-3 pt-1">
        {onBack && <BackBtn onClick={onBack} />}
        <Btn onClick={cont} disabled={saving} data-testid="wizard-photos-next">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continuer <ArrowRight className="w-4 h-4" /></Btn>
      </div>
    </div>
  );
}

function StepStaging({ slug, listing, reload, firstListing, onPublished, onNext, onBack }) {
  const [regen, setRegen] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [pubErr, setPubErr] = useState("");
  useEffect(() => { authApi.creditsConfig().then(setCfg).catch(() => {}); }, []);
  const stagings = (listing?.staging || []);
  const regenerate = async (id) => {
    setRegen(id);
    try { await authApi.regenerateStaging(slug, id); await reload(); } catch (_) {}
    setRegen(null);
  };
  const remove = async (id) => { try { await authApi.deleteStaging(slug, id); await reload(); } catch (_) {} };
  // Formule minimale suffisante pour le nombre de rendus (défaut Classique = 1 crédit = crédit offert).
  const pickTier = () => {
    const tiers = cfg?.tiers || {};
    const keys = ["classique", "pro", "premium"].filter((k) => tiers[k]);
    if (!keys.length) return "classique";
    return keys.find((k) => (tiers[k].max_staging || 0) >= stagings.length) || keys[keys.length - 1];
  };
  const publishFree = async () => {
    setPublishing(true); setPubErr("");
    try { await authApi.publishListing(slug, pickTier()); await reload(); onPublished && onPublished(); }
    catch (e) { setPubErr(e.detail?.message || e.message || "Publication impossible."); setPublishing(false); }
  };
  return (
    <div className="space-y-4" data-testid="wizard-step-staging">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-3"><Home className="w-7 h-7 text-[#C19A6B]" /></div>
        <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Home Staging</h3>
        <p className="text-sm text-zinc-400 mt-1.5">Choisissez les photos et les styles. La bonne formule est sélectionnée automatiquement selon le nombre de rendus — vous ne payez que ce qu'il faut.</p>
        {firstListing ? (
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C19A6B]" data-testid="staging-free-badge">
            <Sparkles className="w-3.5 h-3.5" /> Annonce test offerte — génération incluse
          </p>
        ) : (
          <button type="button" onClick={() => setShowBuy(true)} data-testid="staging-buy-credits" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] transition-colors">
            <Coins className="w-3.5 h-3.5" /> Pas assez de crédits ? Recharger / passer à Premium
          </button>
        )}
      </div>

      {showBuy && (
        <BuyCreditsModal config={cfg} pendingSlug={slug} pendingTier="premium"
          onClose={() => setShowBuy(false)}
          onCredited={() => { setShowBuy(false); reload && reload(); }} />
      )}

      {/* 1re annonce : écran de félicitations dès la 1re génération + publication gratuite express */}
      {firstListing && stagings.length > 0 && (() => {
        const { score } = getScore(listing);
        return (
          <div className="border border-[#C19A6B]/50 bg-gradient-to-br from-[#C19A6B]/15 to-[#C19A6B]/5 rounded-2xl p-5 text-center" data-testid="wizard-first-celebration">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#C19A6B]/20 flex items-center justify-center mb-2"><PartyPopper className="w-6 h-6 text-[#C19A6B]" /></div>
            <p className="calli text-2xl text-[#C19A6B] leading-tight">Bravo, votre Annonce test est prête !</p>
            <p className="text-sm text-zinc-300 mt-1">Vos rendus IA sont générés. Publiez dès maintenant — c'est offert.</p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <ScoreRing score={score} size={64} />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">Score de qualité</p>
                <p className="text-base font-semibold" style={{ color: scoreColor(score) }}>{scoreLabel(score)}</p>
              </div>
            </div>
            {pubErr && <p className="text-sm text-red-400 mt-3" data-testid="celebration-error">{pubErr}</p>}
            <button type="button" onClick={publishFree} disabled={publishing} data-testid="wizard-celebration-publish"
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-lg transition-colors">
              {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><Rocket className="w-4 h-4" /> Publier gratuitement</>}
            </button>
            <p className="text-[11px] text-zinc-400 mt-2">Vous pouvez aussi continuer pour ajouter d'autres rendus avant de publier.</p>
          </div>
        );
      })()}
      {stagings.length > 0 && (
        <div className="space-y-2" data-testid="wizard-staging-results">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">Rendus générés ({stagings.length}) — validez ou régénérez</p>
            <button onClick={onNext} data-testid="wizard-staging-validate-top" className="inline-flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded transition-colors"><Check className="w-3.5 h-3.5" /> Valider ces photos</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {stagings.map((s) => (
              <div key={s.id} className="relative border border-white/10 rounded-lg overflow-hidden" data-testid={`staging-render-${s.id}`}>
                <img src={s.after} alt="" className="w-full h-28 object-cover" />
                {regen === s.id && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C19A6B]" /></div>}
                <div className="absolute inset-x-0 bottom-0 flex">
                  <button onClick={() => regenerate(s.id)} disabled={regen === s.id} data-testid={`staging-regen-${s.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 bg-black/70 hover:bg-[#C19A6B] text-white text-[10px] uppercase tracking-wider py-1.5 transition-colors">
                    <RefreshCw className="w-3 h-3" /> Régénérer
                  </button>
                  <button onClick={() => remove(s.id)} disabled={regen === s.id} data-testid={`staging-del-${s.id}`}
                    className="inline-flex items-center justify-center bg-black/70 hover:bg-red-500 text-white px-2 transition-colors"><Trash2 className="w-3 h-3" /></button>
                </div>
                {s.style && <span className="absolute top-1 left-1 bg-black/60 text-[9px] text-[#C19A6B] px-1.5 py-0.5 rounded uppercase tracking-wider">{s.style}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] uppercase tracking-wider text-zinc-500 pt-2">Générer des rendus home staging</p>
      <OneClickGenerate slug={slug} images={(listing && listing.images) || []} defaultIncludeText={false} compact autoPack hideUpsell={firstListing} onDone={() => reload && reload()} />

      <div className="flex items-center justify-center gap-3 pt-1">
        {onBack && <BackBtn onClick={onBack} />}
        <button onClick={onNext} data-testid="wizard-staging-skip" className="text-sm text-zinc-400 hover:text-white uppercase tracking-wider">Passer cette étape</button>
        <Btn onClick={onNext} data-testid="wizard-staging-next">Valider &amp; continuer <ArrowRight className="w-4 h-4" /></Btn>
      </div>
    </div>
  );
}

function StepReview({ slug, listing, firstListing, onEdit, reload, onNext, onBack }) {
  const [cfg, setCfg] = useState(null);
  const [pack, setPack] = useState(listing?.tier || "classique");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showBuy, setShowBuy] = useState(false);
  useEffect(() => { authApi.creditsConfig().then(setCfg).catch(() => {}); }, []);
  useEffect(() => { if (listing?.tier) setPack(listing.tier); }, [listing]);
  const tiers = cfg?.tiers || {};
  const stagingCount = ((listing?.staging_images || listing?.staging || []) || []).length;
  const PACK_KEYS = ["classique", "pro", "premium"].filter((k) => tiers[k]);
  const reloadCfg = () => authApi.creditsConfig().then(setCfg).catch(() => {});

  const publish = async () => {
    setBusy(true); setErr("");
    try { await authApi.publishListing(slug, pack); await reload(); onNext(); }
    catch (e) {
      const d = e.detail || {};
      const msg = d.message || e.message || "Publication impossible.";
      setErr(msg);
      setBusy(false);
      // Crédits insuffisants → ouvrir directement l'achat/upgrade
      if (/crédit|credit|insuffis|solde/i.test(msg)) setShowBuy(true);
    }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-review">
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-3"><Eye className="w-7 h-7 text-[#C19A6B]" /></div>
        <h3 className="text-xl font-light text-white">Vérification finale</h3>
        <p className="text-sm text-zinc-400 mt-1.5">Prévisualisez et modifiez librement avant de publier.</p>
      </div>

      {/* Score de qualité global de l'annonce */}
      {(() => {
        const { score } = getScore(listing);
        return (
          <div className="border border-white/10 rounded-xl p-4 flex items-center gap-4" data-testid="wizard-review-score">
            <ScoreRing score={score} size={68} />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Score de qualité</p>
              <p className="text-base font-semibold" style={{ color: scoreColor(score) }}>{scoreLabel(score)}</p>
              <p className="text-[12px] text-zinc-400 mt-0.5">Une annonce complète est mieux référencée et attire plus d'acquéreurs.</p>
            </div>
          </div>
        );
      })()}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <a href={`/bien/${slug}`} target="_blank" rel="noopener noreferrer" data-testid="wizard-preview-link" className="inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-sm uppercase tracking-wider px-4 py-2.5 rounded-lg"><Eye className="w-4 h-4" /> Prévisualiser</a>
        <button onClick={onEdit} data-testid="wizard-edit-btn" className="inline-flex items-center gap-2 border border-white/20 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] text-sm uppercase tracking-wider px-4 py-2.5 rounded-lg">Modifier l'annonce</button>
      </div>

      {/* Alerte doré : infos manquantes ou douteuses à corriger avant publication */}
      {(() => {
        const warnings = getChecklist(listing).filter((c) => !c.ok);
        return warnings.length > 0 ? (
          <div className="border border-[#C19A6B]/50 bg-[#C19A6B]/10 rounded-xl p-4" data-testid="wizard-review-alert">
            <p className="text-sm font-semibold text-[#C19A6B] flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> À vérifier avant de publier</p>
            <ul className="mt-2 space-y-1.5">
              {warnings.map((w) => (
                <li key={w.key} data-testid={`review-warn-${w.key}`} className="text-[13px] text-[#C19A6B] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C19A6B] shrink-0" /> {w.hint}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-zinc-400 mt-2.5">Vous pouvez publier malgré tout, mais compléter ces informations améliore nettement la visibilité de votre annonce.</p>
          </div>
        ) : (
          <p className="text-sm text-[#C19A6B] flex items-center gap-2 justify-center" data-testid="wizard-review-ok"><Check className="w-4 h-4" /> Toutes les informations essentielles sont complètes.</p>
        );
      })()}

      {/* Choix du type d'annonce + coût (dépend du nombre de photos home-stagées) */}
      <div className="border border-white/10 rounded-xl p-4 space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400">Type d'annonce & publication {stagingCount ? `· ${stagingCount} rendu${stagingCount > 1 ? "s" : ""} home staging` : ""}</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {PACK_KEYS.map((k) => {
            const t = tiers[k]; const need = stagingCount > (t.max_staging || 0);
            return (
              <button key={k} type="button" onClick={() => setPack(k)} disabled={need} data-testid={`wizard-pack-${k}`}
                className={`text-left border p-3 rounded-lg transition-colors ${pack === k ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/15 hover:border-white/30"} ${need ? "opacity-40 cursor-not-allowed" : ""}`}>
                <span className={`text-sm font-semibold ${pack === k ? "text-[#C19A6B]" : "text-white"}`}>{t.label}</span>
                <span className="block text-[11px] text-zinc-400 mt-0.5">{t.cost} crédit{t.cost > 1 ? "s" : ""} · {t.max_staging} rendus max</span>
                {need && <span className="block text-[10px] text-amber-400 mt-0.5">Insuffisant pour {stagingCount} rendus</span>}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-[#C19A6B] flex items-center gap-1.5"><Coins className="w-3.5 h-3.5" /> Coût de publication : {tiers[pack]?.cost || 1} crédit{(tiers[pack]?.cost || 1) > 1 ? "s" : ""} (débité à la mise en ligne).</p>
        {firstListing ? (
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C19A6B]" data-testid="wizard-free-badge">
            <Sparkles className="w-3.5 h-3.5" /> Annonce test offerte — publication incluse
          </p>
        ) : (
          <button type="button" onClick={() => setShowBuy(true)} data-testid="wizard-upgrade-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] transition-colors">
            <Coins className="w-3.5 h-3.5" /> Recharger / passer à une formule supérieure
          </button>
        )}
      </div>

      {showBuy && (
        <BuyCreditsModal config={cfg} pendingSlug={slug} pendingTier={pack}
          onClose={() => setShowBuy(false)}
          onCredited={() => { setShowBuy(false); setErr(""); reloadCfg(); reload && reload(); }} />
      )}

      {err && <p className="text-sm text-red-400 text-center" data-testid="wizard-publish-error">{err}</p>}
      <div className="flex items-center justify-center gap-3 pt-1">
        {onBack && <BackBtn onClick={onBack} />}
        <Btn onClick={publish} disabled={busy} data-testid="wizard-publish-btn">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><Rocket className="w-4 h-4" /> Publier l'annonce</>}</Btn>
      </div>
    </div>
  );
}

function StepPublished({ slug, onShare, onClose }) {
  return (
    <div className="text-center py-6" data-testid="wizard-step-published">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-4"><PartyPopper className="w-8 h-8 text-[#C19A6B]" /></div>
      <h3 className="text-xl font-light text-white">Félicitations ! Votre annonce est désormais en ligne.</h3>
      <p className="text-sm text-zinc-400 mt-2 mb-6">Maximisez sa visibilité : partagez-la dès maintenant sur vos réseaux sociaux.</p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Btn onClick={onShare} data-testid="wizard-share-btn"><Share2 className="w-4 h-4" /> Partager sur mes réseaux</Btn>
        <button onClick={onClose} data-testid="wizard-finish-btn" className="text-sm text-zinc-400 hover:text-white uppercase tracking-wider">Terminer</button>
      </div>
    </div>
  );
}

export default PublishWizard;
